import React, { useMemo, useState, useEffect } from 'react'
import { Html } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { getLotImage } from '../../utils/lotImageExtractor'

export default function HoverTooltip({ region, position, activeStage, stages, backgroundImage, svgBounds, shape }) {
  if (!region || !position) return null

  const isStageSelected = Boolean(activeStage)
  const stageKey = useMemo(() => {
    if (!region) return null
    const key = (region?.name && String(region.name).trim()) || 'Ungrouped'
    return key
  }, [region])

  const lotsInStage = useMemo(() => {
    if (!stages || !stages.bucket || !stageKey) return null
    return stages.bucket[stageKey] || 0
  }, [stages, stageKey])

  // When a stage is active, only show tooltip for lots in the active stage
  const isInActiveStage = isStageSelected && stageKey === activeStage

  // State for lot image
  const [lotImage, setLotImage] = useState(null)
  const [imageLoading, setImageLoading] = useState(false)
  const [imageAspectRatio, setImageAspectRatio] = useState(null)
  
  const [tooltipDirection, setTooltipDirection] = useState({ horizontal: 'right', vertical: 'bottom' })
  const { camera, size } = useThree()

  // Extract lot image when region changes and we have the necessary data
  useEffect(() => {
    // Clear previous image immediately when region changes
    setLotImage(null)
    setImageLoading(false)
    setImageAspectRatio(null)
    
    if (!region || !backgroundImage || !svgBounds || !shape || !isInActiveStage) {
      return
    }

    let isCancelled = false

    const extractImage = async () => {
      setImageLoading(true)
      try {
        // Create a more unique cache key that includes shape data
        const shapeHash = shape ? JSON.stringify(shape.getPoints().slice(0, 10)) : 'no-shape'
        const cacheKey = `lot-${region.id}-${backgroundImage.src}-${shapeHash}`
        
        
        const imageDataURL = await getLotImage(cacheKey, backgroundImage, svgBounds, shape, 150)
        
        // Only update state if this effect hasn't been cancelled
        if (!isCancelled) {
          
          // Validate data URL format
          if (imageDataURL && !imageDataURL.startsWith('data:image/')) {
            console.error('Invalid data URL format:', imageDataURL.substring(0, 20))
          }
          
          setLotImage(imageDataURL)
          
          // Calculate aspect ratio from the cropped image
          if (imageDataURL) {
            const img = new Image()
            img.onload = () => {
              const aspectRatio = img.width / img.height
              setImageAspectRatio(aspectRatio)
            }
            img.src = imageDataURL
          }
        }
      } catch (error) {
        console.warn('Failed to extract lot image:', error)
        if (!isCancelled) {
          setLotImage(null)
        }
      } finally {
        if (!isCancelled) {
          setImageLoading(false)
        }
      }
    }

    extractImage()

    // Cleanup function to cancel the operation if region changes
    return () => {
      isCancelled = true
    }
  }, [region, backgroundImage, svgBounds, shape, isInActiveStage])

  // Auto-flipping tooltip positioning based on screen space
  useEffect(() => {
    if (!position) return

    // Use different box estimates for stage vs lot tooltips
    const BOX_WIDTH = isInActiveStage ? 440 : 320
    const BOX_HEIGHT = isInActiveStage ? 240 : 180
    const SIDEBAR_WIDTH = 360
    const padding = 16

    // Project world position to screen pixels
    const vec = new THREE.Vector3(position.x, position.y, 0)
    vec.project(camera)
    const screenX = (vec.x + 1) / 2 * size.width
    const screenY = (1 - (vec.y + 1) / 2) * size.height // top-left origin

    const spaceOnRight = (size.width - SIDEBAR_WIDTH) - screenX
    const horizontalDirection = (spaceOnRight < BOX_WIDTH + padding) ? 'left' : 'right'
    const spaceBelow = size.height - screenY
    const spaceAbove = screenY
    const verticalDirection = (spaceBelow < BOX_HEIGHT + padding && spaceAbove > BOX_HEIGHT + padding) ? 'top' : 'bottom'

    setTooltipDirection({ horizontal: horizontalDirection, vertical: verticalDirection })
  }, [position, camera, size, isInActiveStage])

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (position) {
        // Trigger recalculation by updating direction state
        setTooltipDirection(prev => ({ ...prev }))
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [position])

  const GAP = 8
  const percX = tooltipDirection.horizontal === 'left' ? '-100%' : '0'
  const percY = tooltipDirection.vertical === 'top' ? '-100%' : '0'
  const pxX = (tooltipDirection.horizontal === 'left' ? -GAP : GAP)
  const pxY = (tooltipDirection.vertical === 'top' ? -GAP : GAP)

  return (
    <Html position={[position.x, position.y, 0]} style={{ pointerEvents: 'none' }}>
      <div className={`hover-tooltip tooltip-${tooltipDirection.horizontal}-${tooltipDirection.vertical}`} style={{ transform: `translate(${percX}, ${percY}) translate(${pxX}px, ${pxY}px)` }}>
        {!isStageSelected ? (

          
          <>
            <div><strong>Stage</strong>: {stageKey}</div>
            <div><strong>Lots</strong>: {lotsInStage != null ? lotsInStage : '-'}</div>
          </>
        ) : isInActiveStage ? (
          <div className="lot-tooltip-modern">
            {/* Top Row: Lot Number and Status */}
            <div className="lot-tooltip-header">
              <div className="lot-number-large">Lot {region.lotNumber || '-'}</div>
              <div className="lot-status-circle" data-status={region.status || 'Available'}>
                <span className="status-text">{region.status || 'Available'}</span>
              </div>
              {/* Debug: Show actual status value */}
            </div>
            
            {/* Bottom Row: Image and Details */}
            <div className="lot-tooltip-content">
              {/* Lot Image */}
              <div className={`lot-image-container ${imageAspectRatio ? (imageAspectRatio > 1.2 ? 'landscape' : imageAspectRatio < 0.8 ? 'portrait' : 'square') : ''}`}>
                {lotImage ? (
                  <img 
                    src={lotImage} 
                    alt={`Lot ${region.lotNumber || region.id}`}
                    className="lot-image"
                    onLoad={() => {}}
                    onError={(e) => console.error('Image failed to load:', e)}
                  />
                ) : imageLoading ? (
                  <div className="lot-image-loading">Loading...</div>
                ) : (
                  <div className="lot-image-placeholder">No Image</div>
                )}
              </div>
              
              {/* Details - stacked, each on a single line */}
              <div className="lot-details">
                {region.lotSize && <div className="lot-size lot-details-line">{region.lotSize}</div>}
                {region.price && <div className="lot-price lot-details-line">${Number(region.price).toLocaleString()}</div>}
                {region.customProps?.frontage && <div className="lot-frontage lot-details-line">Frontage: {region.customProps.frontage}</div>}
                {region.customProps?.aspect && <div className="lot-aspect lot-details-line">Aspect: {region.customProps.aspect}</div>}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div><strong>Stage</strong>: {stageKey}</div>
            <div><strong>Lots</strong>: {lotsInStage != null ? lotsInStage : '-'}</div>
          </>
        )}
      </div>
    </Html>
  )
}


